import React, { useState } from 'react';
import { QrCode, ShieldCheck, ShieldOff, Smartphone } from 'lucide-react';
import Alert from '../components/Alert';
import Modal from '../components/Modal';
import { authApi, getTwoFactorEnabledFromToken } from '../services/api';

const getApiErrorMessage = (err: any, fallback: string) => {
    const translatedMessage = err?.response?.data?.translatedMessage;
    if (typeof translatedMessage === 'string' && translatedMessage.trim()) {
        return translatedMessage;
    }

    const apiMessage = err?.response?.data?.message;
    if (typeof apiMessage === 'string' && apiMessage.trim()) {
        return apiMessage;
    }

    if (Array.isArray(apiMessage)) {
        const joined = apiMessage
            .map((item: { message?: string }) => item?.message)
            .filter((message: string | undefined): message is string => Boolean(message))
            .join(' ');

        if (joined) {
            return joined;
        }
    }

    return fallback;
};

const Security: React.FC = () => {
    const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
    const [loadingAction, setLoadingAction] = useState<'enable' | 'disable' | null>(null);
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [twoFactorEnabled, setTwoFactorEnabled] = useState<boolean>(() => getTwoFactorEnabledFromToken());

    const handleEnable = async () => {
        setLoadingAction('enable');
        setFeedback(null);
        try {
            const response = await authApi.enableTwoFactor();
            setQrDataUrl(response.data.qrDataUrl);
            setTwoFactorEnabled(true);
            setFeedback({
                type: 'success',
                message: 'Autenticação em dois fatores ativada. Escaneie o QR code para concluir a configuração no autenticador.',
            });
        } catch (err: any) {
            setFeedback({
                type: 'error',
                message: getApiErrorMessage(err, 'Não foi possível ativar a autenticação em dois fatores.'),
            });
        } finally {
            setLoadingAction(null);
        }
    };

    const handleDisable = async () => {
        setLoadingAction('disable');
        setFeedback(null);
        try {
            await authApi.disableTwoFactor();
            setQrDataUrl(null);
            setTwoFactorEnabled(false);
            setFeedback({
                type: 'success',
                message: 'Autenticação em dois fatores desativada com sucesso.',
            });
        } catch (err: any) {
            setFeedback({
                type: 'error',
                message: getApiErrorMessage(err, 'Não foi possível desativar a autenticação em dois fatores.'),
            });
        } finally {
            setLoadingAction(null);
        }
    };

    return (
        <div className="space-y-6">
            <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-8 text-white">
                <div className="flex items-center gap-4">
                    <div className="rounded-2xl bg-white/10 p-3">
                        <ShieldCheck className="h-8 w-8" />
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold">Segurança</h1>
                        <p className="text-primary-100 mt-2">
                            Gerencie a autenticação em dois fatores da sua conta.
                        </p>
                    </div>
                </div>
            </div>

            {feedback && (
                <Alert
                    type={feedback.type}
                    title={feedback.type === 'success' ? 'Operação realizada' : 'Não foi possível concluir'}
                    message={feedback.message}
                />
            )}

            <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
                <section className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
                    <div className="flex items-start gap-4">
                        <div className="rounded-2xl bg-primary-50 p-3">
                            <Smartphone className="h-6 w-6 text-primary-600" />
                        </div>
                        <div className="flex-1">
                            <div className="flex flex-wrap items-center gap-3">
                                <h2 className="text-lg font-semibold text-secondary-900">Autenticação em dois fatores</h2>
                                <span
                                    className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                                        twoFactorEnabled
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-secondary-200 text-secondary-700'
                                    }`}
                                >
                                    {twoFactorEnabled ? 'Ativa' : 'Inativa'}
                                </span>
                            </div>
                            <p className="mt-1 text-sm text-secondary-600">
                                {twoFactorEnabled
                                    ? 'Sua conta já exige um código temporário de 6 dígitos no login.'
                                    : 'Ao ativar, o login passa a exigir um código temporário de 6 dígitos gerado no seu aplicativo autenticador.'}
                            </p>
                        </div>
                    </div>

                    <div className="mt-6 rounded-2xl border border-secondary-200 bg-secondary-50 p-5">
                        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <div className="text-sm font-medium text-secondary-900">Controle rápido</div>
                                <p className="mt-1 text-sm text-secondary-600">
                                    {twoFactorEnabled
                                        ? 'A autenticação em dois fatores está ativa. Você pode desativar se quiser remover essa camada extra de segurança.'
                                        : 'Ative para gerar um novo QR code e concluir o pareamento no aplicativo autenticador.'}
                                </p>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="button"
                                    onClick={handleEnable}
                                    className="btn-primary min-w-[148px]"
                                    disabled={loadingAction !== null || twoFactorEnabled}
                                >
                                    {loadingAction === 'enable' ? 'Ativando...' : 'Ativar 2FA'}
                                </button>
                                <button
                                    type="button"
                                    onClick={handleDisable}
                                    className="btn-secondary min-w-[148px] inline-flex items-center justify-center gap-2"
                                    disabled={loadingAction !== null || !twoFactorEnabled}
                                >
                                    <ShieldOff className="h-4 w-4" />
                                    {loadingAction === 'disable' ? 'Desativando...' : 'Desativar'}
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                <aside className="bg-white rounded-xl border border-secondary-200 shadow-sm p-6">
                    <div className="flex items-center gap-3">
                        <div className="rounded-2xl bg-secondary-100 p-3">
                            <QrCode className="h-6 w-6 text-secondary-700" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-secondary-900">Pareamento</h2>
                            <p className="text-sm text-secondary-600">
                                {twoFactorEnabled
                                    ? 'O QR code foi exibido no modal durante a ativação. Se precisar reconfigurar, desative e reative o 2FA.'
                                    : 'O QR code será exibido no modal após a ativação.'}
                            </p>
                        </div>
                    </div>

                    <div className="mt-6 flex min-h-[280px] items-center justify-center rounded-2xl border border-dashed border-secondary-300 bg-secondary-50 p-6 text-center">
                        <p className="max-w-xs text-sm text-secondary-500">
                            {twoFactorEnabled
                                ? 'O QR code foi exibido no modal durante a ativação. Se precisar reconfigurar, desative e reative o 2FA.'
                                : 'Quando a autenticação em dois fatores for ativada, o QR code será exibido no modal para leitura no aplicativo autenticador.'}
                        </p>
                    </div>
                </aside>
            </div>

            <Modal
                isOpen={Boolean(qrDataUrl)}
                onClose={() => setQrDataUrl(null)}
                title="Escaneie o QR code"
            >
                <div className="space-y-4">
                    <p className="text-sm text-secondary-600">
                        Use seu aplicativo autenticador para escanear o QR code abaixo. Depois disso, o login passará a solicitar o código temporário.
                    </p>
                    {qrDataUrl && (
                        <div className="flex justify-center rounded-2xl bg-secondary-50 p-4">
                            <img
                                src={qrDataUrl}
                                alt="QR code para autenticação em dois fatores"
                                className="max-h-80 rounded-2xl border border-secondary-200 bg-white p-3"
                            />
                        </div>
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default Security;
